import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Col,
} from 'reactstrap';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { safeExternalUrl } from '../../utils/safeUrl';
import { useRetryDelisting } from './useNexusPostmaster';
import {
  useDeleteRblIp,
  useRblIps,
  useRblStatus,
  useTriggerRblScan,
  useUpsertRblIp,
} from './useNexusRbl';

export function RblServerIpsTab({ canWrite }) {
  const ips = useRblIps();
  const status = useRblStatus();
  const [selectedIp, setSelectedIp] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const ipsByAddr = new Map((ips.data?.ips ?? []).map((r) => [r.ip, r]));
  const statusByAddr = new Map((status.data?.rows ?? []).map((r) => [r.ip_address, r]));

  const merged = (ips.data?.ips ?? []).map((row) => ({
    ...row,
    status: statusByAddr.get(row.ip) ?? null,
  }));
  // status-only IPs (Backend kennt sie, monitored_ips noch nicht): trotzdem zeigen
  for (const s of status.data?.rows ?? []) {
    if (!ipsByAddr.has(s.ip_address)) {
      merged.push({
        ip: s.ip_address,
        label: s.label,
        source: 'unknown',
        last_scanned_at: s.listings?.[0]?.last_checked_at,
        status: s,
      });
    }
  }

  return (
    <Row className="g-3">
      <Col lg="7">
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-uppercase small text-muted mb-0">Gemonitorte IPs</h6>
              <HasPermission perms={['integrations.write']}>
                {canWrite && (
                  <Button size="sm" color="primary" onClick={() => setShowAdd(true)}>
                    + IP
                  </Button>
                )}
              </HasPermission>
            </div>
            <Alert color="info" className="small">
              Status pollt alle 30 s. Nexus-RBL-Cron läuft stündlich — die
              Tabelle spiegelt den letzten gespeicherten Stand.
            </Alert>
            <DataTable
              loading={ips.isLoading || status.isLoading}
              error={ips.error ?? status.error}
              empty={!ips.isLoading && merged.length === 0}
              emptyMessage="Noch keine IPs konfiguriert."
              onRetry={() => { ips.refetch(); status.refetch(); }}
            >
              <thead>
                <tr>
                  <th style={{ width: 160 }}>IP</th>
                  <th>Label</th>
                  <th style={{ width: 100 }}>Quelle</th>
                  <th style={{ width: 130 }}>Letzter Scan</th>
                  <th style={{ width: 150 }}>Status</th>
                  <th style={{ width: 200 }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {merged.map((row) => (
                  <IpRow
                    key={row.ip}
                    row={row}
                    canWrite={canWrite}
                    onOpen={() => setSelectedIp(row)}
                  />
                ))}
              </tbody>
            </DataTable>
          </CardBody>
        </Card>
      </Col>

      <Col lg="5">
        <IpStatusPanel ip={selectedIp} canWrite={canWrite} />
      </Col>

      <AddIpModal isOpen={showAdd} onClose={() => setShowAdd(false)} />

      <IpDetailDrawer
        ip={selectedIp}
        isOpen={!!selectedIp}
        onClose={() => setSelectedIp(null)}
        canWrite={canWrite}
      />
    </Row>
  );
}

function IpRow({ row, canWrite, onOpen }) {
  const scan = useTriggerRblScan();
  const del = useDeleteRblIp();
  const s = row.status;
  const dot = s?.clean === false ? '🔴' : s?.clean === true ? '🟢' : '⚪';
  const label = s?.clean === false
    ? `${s.listings.length} Listing${s.listings.length === 1 ? '' : 's'}`
    : s?.clean === true
      ? 'sauber'
      : 'unbekannt';
  return (
    <tr role="button" onClick={onOpen} style={{ cursor: 'pointer' }}>
      <td className="font-monospace small">{row.ip}</td>
      <td className="small">{row.label ?? '—'}</td>
      <td className="small">
        <Badge color="secondary" pill className="orbit-code-badge-sm">{row.source ?? '—'}</Badge>
      </td>
      <td>
        {row.last_scanned_at ? (
          <RelativeTime iso={row.last_scanned_at} />
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="small">{dot} {label}</td>
      <td>
        <HasPermission perms={['integrations.write']}>
          {canWrite && (
            <ButtonGroup size="sm" onClick={(e) => e.stopPropagation()}>
              <Button
                color="info"
                outline
                onClick={() => scan.mutate({ ip: row.ip })}
                disabled={scan.isPending}
              >
                Scan now
              </Button>
              <Button
                color="danger"
                outline
                onClick={() => del.mutate({ ip: row.ip })}
                disabled={del.isPending}
                title="Aus Monitoring entfernen"
              >
                ✕
              </Button>
            </ButtonGroup>
          )}
        </HasPermission>
      </td>
    </tr>
  );
}

function IpStatusPanel({ ip }) {
  if (!ip) {
    return (
      <Card>
        <CardBody className="text-muted small">
          IP in der Liste anklicken für Listing-Details.
        </CardBody>
      </Card>
    );
  }
  return (
    <Card>
      <CardBody>
        <h6 className="text-uppercase small text-muted mb-2">
          {ip.ip} · {ip.label ?? '—'}
        </h6>
        <ListingsList listings={ip.status?.listings ?? []} clean={ip.status?.clean} />
      </CardBody>
    </Card>
  );
}

function ListingsList({ listings, clean }) {
  if (clean === true || listings.length === 0) {
    return <p className="small text-success mb-0">🟢 Keine aktiven Listings.</p>;
  }
  return (
    <ul className="list-unstyled mb-0">
      {listings.map((l) => (
        <li key={`${l.provider}-${l.return_code}`} className="mb-2 small">
          <div className="fw-semibold">
            {l.provider_name ?? l.provider}{' '}
            <code className="text-muted">{l.dns_zone}</code>
          </div>
          <div>
            Return-Code: <code>{l.return_code}</code>
            {l.return_label && <span className="text-muted"> ({l.return_label})</span>}
          </div>
          <div className="text-muted">
            Erstmals gesehen: {l.first_seen_at ? <RelativeTime iso={l.first_seen_at} /> : '—'}
            {' · '}Letzter Check: {l.last_checked_at ? <RelativeTime iso={l.last_checked_at} /> : '—'}
          </div>
          {safeExternalUrl(l.lookup_url) && (
            <a href={safeExternalUrl(l.lookup_url)} target="_blank" rel="noreferrer noopener" className="small">
              Lookup öffnen ↗
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function IpDetailDrawer({ ip, isOpen, onClose, canWrite }) {
  const delist = useRetryDelisting();

  if (!ip) return null;
  const listings = ip.status?.listings ?? [];

  return (
    <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 560 }}>
      <OffcanvasHeader toggle={onClose}>
        {ip.ip} · {ip.label ?? '—'}
      </OffcanvasHeader>
      <OffcanvasBody>
        <ListingsList listings={listings} clean={ip.status?.clean} />
        <HasPermission perms={['integrations.write']}>
          {canWrite && listings.length > 0 && (
            <div className="mt-4 pt-3 border-top">
              <h6 className="text-uppercase small text-muted">Delisting</h6>
              <ButtonGroup className="d-flex flex-wrap">
                {listings.map((l) => (
                  <Button
                    key={`del-${l.provider}`}
                    color="info"
                    outline
                    onClick={() =>
                      // retry-delisting nimmt eine incident-id; auf der Status-Sicht
                      // fehlt die. Backend akzeptiert auch `{ ip, provider }` als
                      // Job-Trigger laut Spec — der Sub-Plugin-Pfad ist ohnehin
                      // noch Folge-Ticket (siehe Postmaster-INBOX-Hinweis).
                      delist.mutate({ id: `${ip.ip}/${l.provider}` })
                    }
                    disabled={delist.isPending}
                  >
                    {l.provider_name ?? l.provider}
                  </Button>
                ))}
              </ButtonGroup>
              <div className="small text-muted mt-2">
                Sub-Plugins für automatisches Delisting (Microsoft / Barracuda /
                Spamhaus) sind Folge-Tickets — der Status-Flip auf
                <code> delisting_requested</code> läuft schon.
              </div>
            </div>
          )}
        </HasPermission>
      </OffcanvasBody>
    </Offcanvas>
  );
}

function AddIpModal({ isOpen, onClose }) {
  const [form, setForm] = useState({ ip: '', label: '', source: 'manual' });
  const upsert = useUpsertRblIp();
  const onSubmit = async (e) => {
    e.preventDefault();
    await upsert.mutateAsync({
      ip: form.ip,
      label: form.label,
      source: form.source,
    });
    setForm({ ip: '', label: '', source: 'manual' });
    onClose();
  };
  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>IP zur Überwachung hinzufügen</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>IP-Adresse</Label>
            <Input
              type="text"
              value={form.ip}
              onChange={(e) => setForm((f) => ({ ...f, ip: e.target.value }))}
              placeholder="198.51.100.10"
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Label</Label>
            <Input
              type="text"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="mail.valitype.de"
            />
          </FormGroup>
          <FormGroup>
            <Label>Quelle</Label>
            <Input
              type="select"
              value={form.source}
              onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
            >
              <option value="manual">manual</option>
              <option value="enhance">enhance</option>
              <option value="netbox">netbox</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={upsert.isPending}>
            {upsert.isPending ? 'Speichere…' : 'Hinzufügen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
